import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { FigureOrderBucket } from 'src/app/shared/models/FigureOrderItem';
import { IPrecastRecord } from 'src/app/shared/models/i-precast-record.modal';
import { HttpService } from 'src/app/shared/services/http.service';
import { NativeSoilRecommendationRequest, NativeSoilRecommendationResult } from
  '../models/native-soil-recommendation.model';

@Injectable({
  providedIn: 'root',
})
export class ReportService {
  projectInformation : IPrecastRecord;
  constructor(private httpService: HttpService) { }

  public fetchReportData(projectId: string): Observable<any> {
    const url = `nativereports/getnativesoilreport/${projectId}`;
    return this.httpService.get(url);
  }

  public updateReport(payload: any): Observable<any> {
    return this.httpService.post('nativereports/UpdateReport', payload);
  }

  public addReportAttachments(reportId: string, files: File[]): Observable<void> {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file, file.name));
    return this.httpService.post(`nativereports/AddReportAttachments/${reportId}`, formData, {
      responseType: 'text',
    });
  }

  public updateAttachmentPositions(reportId: string, attachments: string[]): Observable<void> {
    return this.httpService.post(`nativereports/UpdateAttachmentPositions/${reportId}`, attachments, {
      responseType: 'text',
    });
  }

  public deleteAttachments(reportId: string, attachments: string[]): Observable<void> {
    return this.httpService.post(`nativereports/DeleteAttachmens/${reportId}`, attachments, {
      responseType: 'text',
    });
  }

  public getGraphData(projectId: string): Observable<any> {
    const url = `nativereports/getnativegraphdata/${projectId}`;
    return this.httpService.get(url);
  }

  public getReviewAssign(projectId: string): Observable<any> {
    return this.httpService.get(`project/getprojectreiew/${projectId}`);
  }

  public updateNativeSoilWorksheets(
    projectID: string,
    isMetric: boolean,
    figureObject: {
      nativeFigureCurves: {
        groupID: string;
        curvePoints: { moistureContent: number; tr: number }[];
      }[];
    },
  ): Observable<any> {
    return this.httpService.post('nativereports/UpdateNativeSoilWorksheets', {
      projectID,
      isMetric,
      figureObject,
    });
  }

  public orderNativeSoilFigures(
    figureOrdering: { [bucket: number]: FigureOrderBucket },
    projectId: string,
    reordered: boolean,
  ): Observable<any> {
    return this.httpService.post('nativereports/OrderNativeSoilFigures', {
      projectId,
      figureOrdering,
      reordered,
    });
  }

  public getNativeSoilFigureOrderings(projectID: string): Observable<any> {
    return this.httpService.get(`nativereports/GetNativeSoilFigureOrderings/${projectID}`);
  }

  public updateFigureProperty(
    projectID: string,
    figureNumber: number,
    maxX: number | null,
    maxY: number | null,
    curveShown: boolean,
  ): Observable<any> {
    return this.httpService.post('nativereports/UpdateFigureProperty', {
      projectID,
      figureNumber,
      maxX,
      maxY,
      curveShown,
    });
  }

  public updateAllFiguresScales(
    projectID: string,
    maxX: number | null,
    maxY: number | null,
  ): Observable<void> {
    return this.httpService.post('nativereports/UpdateAllFiguresScales', {
      projectID,
      maxX,
      maxY,
    }, {
      responseType: 'text',
    });
  }

  public createNativeSoilGraphs(isMetric: boolean, projectId: string, figureNumber: number): Observable<Blob> {
    return this.httpService.postBlob('nativereports/CreateNativeSoilGraphs', {
      isMetric, projectId, figureNumber,
    });
  }

  public createNativeSoilReportFromData(payload: any): Observable<Blob> {
    return this.httpService.postBlob('nativereports/CreateNativeSoilReportFromData', payload);
  }

  public createNativeSoilReportPDFFromData(payload: any): Observable<Blob> {
    return this.httpService.postBlob('nativereports/CreateNativeSoilReportPDFFromData', payload);
  }

  public getNativeSoilRecommendation(
    request: NativeSoilRecommendationRequest,
  ): Observable<NativeSoilRecommendationResult> {
    return this.httpService
      .post('nativereports/GetNativeSoilRecommendation', request)
      .pipe(map((response) => (typeof response === 'string' ? JSON.parse(response) : response)));
  }
}
